import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  Archive,
  Ban,
  Download,
  EllipsisVertical,
  LucideIcon,
  RefreshCcw,
  Stamp,
  Trash2,
  Undo2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Document, DocumentStatus } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ReprocessCaseDialog } from "~/features/cases/components/ReprocessCaseDialog";
import { useProjectParams } from "~/helpers";

import {
  getListDocumentsQueryKey,
  useGetDocumentDownloadUrl,
} from "~/api/default/default";
import { useRestartDocumentPipeline } from "~/api/documents/documents";
import { useToast } from "~/hooks/use-toast";
import type { DocumentWithBates } from "~/types/bates";
import { trackEvent } from "~/utils/analytics";
import {
  isBatesEligible,
  useApplyBates,
  useIsBatesEnabled,
  useUpdateBatesStatus,
} from "../../hooks/useBatesActions";
import { ArchiveDocumentAlertDialog } from "../ArchiveDocumentAlertDialog";
import { DeleteDocumentAlertDialog } from "../DeleteDocumentAlertDialog";

const AUDIO_EXTENSIONS = [".mp3", ".mp4", ".wav", ".m4a"];

interface CaseDocumentActionsProps {
  document: Document;
  onCancelDraftUpload?: (filename: string) => void;
}

interface ActionItem {
  id: string;
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  className?: string;
  action: () => void;
}

function getBatesActionItems({
  document,
  isBatesEnabled,
  toast,
  formatMessage,
  applyBates,
  updateBatesStatus,
  setDropdownOpen,
  caseId,
}: {
  document: Document;
  isBatesEnabled: boolean;
  toast: ReturnType<typeof useToast>["toast"];
  formatMessage: ReturnType<typeof useIntl>["formatMessage"];
  applyBates: (ids: string[]) => Promise<unknown>;
  updateBatesStatus: (args: {
    documentIds: string[];
    status: "SKIPPED" | "NONE";
  }) => Promise<unknown>;
  setDropdownOpen: (open: boolean) => void;
  caseId: string;
}): (ActionItem | false)[] {
  if (!isBatesEnabled) return [];
  const doc = document as DocumentWithBates;
  const status = doc.batesStampingStatus;
  const isComplete = document.status === DocumentStatus.COMPLETE;

  const handleError = () => {
    toast({
      variant: "destructive",
      title: formatMessage({
        id: "bates.actionError.title",
        defaultMessage: "Bates Action Failed",
      }),
      description: formatMessage({
        id: "bates.actionError.description",
        defaultMessage: "Could not update Bates status. Please try again.",
      }),
    });
  };

  return [
    // "Apply Bates" — available for NONE or DETECTED_EXISTING docs
    isBatesEligible(document) && {
      id: "bates-apply",
      icon: Stamp,
      messageId: "bates.applyBates",
      defaultMessage: "Apply Bates",
      action: async () => {
        setDropdownOpen(false);
        trackEvent({
          eventName: "app/case/actions_menu/bates_apply.button/click",
          properties: { caseId, documentId: document.id },
        });
        try {
          await applyBates([document.id]);
          toast({
            title: formatMessage({
              id: "bates.applySuccess.title",
              defaultMessage: "Bates Numbers Applied",
            }),
            description: formatMessage({
              id: "bates.applySuccess.description",
              defaultMessage:
                "Stamping in progress — numbers will appear shortly.",
            }),
          });
        } catch {
          handleError();
        }
      },
    },
    // "Skip Bates" — mark as skipped
    isBatesEligible(document) && {
      id: "bates-skip",
      icon: Ban,
      messageId: "bates.skip",
      defaultMessage: "Skip Bates",
      action: async () => {
        setDropdownOpen(false);
        trackEvent({
          eventName: "app/case/actions_menu/bates_skip.button/click",
          properties: { caseId, documentId: document.id },
        });
        try {
          await updateBatesStatus({
            documentIds: [document.id],
            status: "SKIPPED",
          });
        } catch {
          handleError();
        }
      },
    },
    // "Dismiss Hint" — clear DETECTED_EXISTING back to NONE
    isComplete &&
      status === "DETECTED_EXISTING" && {
        id: "bates-dismiss",
        icon: Undo2,
        messageId: "bates.dismissHint",
        defaultMessage: "Dismiss Hint",
        action: async () => {
          setDropdownOpen(false);
          trackEvent({
            eventName: "app/case/actions_menu/bates_dismiss.button/click",
            properties: { caseId, documentId: document.id },
          });
          try {
            await updateBatesStatus({
              documentIds: [document.id],
              status: "NONE",
            });
          } catch {
            handleError();
          }
        },
      },
  ];
}

export const CaseDocumentActions: FC<CaseDocumentActionsProps> = ({
  document,
  onCancelDraftUpload,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatMessage } = useIntl();
  const { mutateAsync: getDocumentDownloadUrl } = useGetDocumentDownloadUrl();
  const { mutateAsync: restartDocumentPipeline } = useRestartDocumentPipeline();
  const isBatesEnabled = useIsBatesEnabled();
  const { mutateAsync: applyBates } = useApplyBates();
  const { mutateAsync: updateBatesStatus } = useUpdateBatesStatus();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showArchiveAlertDialog, setShowArchiveAlertDialog] = useState(false);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);

  const hasBookmarks = Boolean(
    document.structuredS3Key && document.structuredS3Key.length > 0
  );

  const isAudioFile = AUDIO_EXTENSIONS.some((ext) =>
    document.filename.toLowerCase().endsWith(ext)
  );

  useEffect(() => {
    if (dropdownOpen) {
      trackEvent({
        eventName: "app/case/actions.button/click",
        properties: { caseId, documentId: document.id },
      });
    }
  }, [dropdownOpen, document.id, caseId]);

  const handleDownloadDocument = async () => {
    trackEvent({
      eventName: "app/case/actions_menu/download.link/click",
      properties: { caseId, documentId: document.id },
    });

    const { url } = await getDocumentDownloadUrl({
      organizationId,
      projectId: caseId,
      documentId: document.id,
      params: {
        update_counter: true,
      },
    });

    window.open(url, "_blank");
  };

  const handleReprocessDirectly = async () => {
    trackEvent({
      eventName: "app/case/actions_menu/reprocess.button/click",
      properties: { caseId, documentId: document.id, mode: "USE_SNOWFLAKE" },
    });

    try {
      await restartDocumentPipeline({
        organizationId,
        projectId: caseId,
        documentId: document.id,
        params: { skipTOC: true },
        data: { password: undefined },
      });

      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      toast({
        title: formatMessage({ id: "success" }),
        description: formatMessage({
          id: "reprocessDocument.toast.success",
          defaultMessage: "Reprocessing started. Updates will appear shortly.",
        }),
      });
    } catch {
      toast({
        variant: "destructive",
        title: formatMessage({ id: "error" }),
        description: formatMessage({
          id: "reprocessDocument.toast.error",
          defaultMessage: "Failed to start reprocessing. Please try again.",
        }),
      });
    }
  };

  const isPasswordProtected =
    (document.status as string) === DocumentStatus.PASSWORD_PROTECTED;

  const handleOpenReprocessDialog = () => {
    trackEvent({
      eventName: "app/case/actions_menu/reprocess.link/click",
      properties: { caseId, documentId: document.id },
    });
    setDropdownOpen(false);

    if (hasBookmarks || isPasswordProtected) {
      setShowReprocessDialog(true);
    } else {
      handleReprocessDirectly();
    }
  };

  const documentProcessingStarted =
    document.status === DocumentStatus.PROCESSING ||
    document.status === DocumentStatus.COMPLETE ||
    document.status === DocumentStatus.ERROR ||
    document.status === DocumentStatus.PASSWORD_PROTECTED;

  const actionItems = [
    document.status === DocumentStatus.DRAFT && {
      id: "delete",
      icon: Trash2,
      messageId: "caseDocument.actionsDropdown.delete",
      action: () => {
        if (document.id.startsWith("queued-")) {
          onCancelDraftUpload?.(document.filename);
        } else {
          setShowDeleteAlertDialog(true);
        }
      },
      defaultMessage: "Delete",
    },
    documentProcessingStarted &&
      !isAudioFile && {
        id: "retry",
        icon: RefreshCcw,
        messageId: "caseDocument.actionsDropdown.retry",
        action: handleOpenReprocessDialog,
        defaultMessage: "Reprocess",
      },
    documentProcessingStarted && {
      id: "download",
      icon: Download,
      messageId: "caseDocument.actionsDropdown.download",
      action: handleDownloadDocument,
      defaultMessage: "Download",
    },
    document.status !== DocumentStatus.DRAFT && {
      id: "archive",
      icon: Archive,
      messageId: "archive",
      action: () => {
        trackEvent({
          eventName: "app/case/actions_menu/archive.link/click",
          properties: { caseId, documentId: document.id },
        });
        setShowArchiveAlertDialog(true);
      },
      defaultMessage: "Archive",
    },
    // Per-file Bates actions
    ...getBatesActionItems({
      document,
      isBatesEnabled,
      toast,
      formatMessage,
      applyBates,
      updateBatesStatus,
      setDropdownOpen,
      caseId,
    }),
  ].filter(Boolean) as ActionItem[];

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={"hover:opacity-80 rounded-full"}
          >
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actionItems.map((actionItem) => {
            return (
              <DropdownMenuItem
                key={actionItem.id}
                className={clsx("cursor-pointer", actionItem.className)}
                onClick={actionItem.action}
                destructive={actionItem.id === "delete"}
              >
                <actionItem.icon className="mr-2 h-4 w-4" />
                <FormattedMessage
                  id={actionItem.messageId}
                  defaultMessage={actionItem.defaultMessage}
                />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {showArchiveAlertDialog && (
        <ArchiveDocumentAlertDialog
          open={showArchiveAlertDialog}
          setOpen={setShowArchiveAlertDialog}
          documentId={document.id}
          documentName={document.filename}
          caseId={caseId}
        />
      )}
      {showDeleteAlertDialog && (
        <DeleteDocumentAlertDialog
          open={showDeleteAlertDialog}
          setOpen={setShowDeleteAlertDialog}
          documentId={document.id}
          documentName={document.filename}
          caseId={caseId}
        />
      )}
      {showReprocessDialog && (
        <ReprocessCaseDialog
          open={showReprocessDialog}
          setOpen={setShowReprocessDialog}
          documentId={document.id}
          hasBookmarks={hasBookmarks}
          isPasswordProtected={isPasswordProtected}
        />
      )}
    </>
  );
};
